import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HandsetDialogService } from './handset-dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProjectSelectionDialogComponent } from '../components/shared/dialogs/project-selection-dialog/project-selection-dialog.component';
import {
  NewProjectDialogComponent,
  NewProjectData,
} from '../components/project/settings/new-project-dialog/new-project-dialog.component';
import { RbacService } from './rbac.service';
import { ProjectCreationService } from './project-creation.service';
import { FirebaseAuthService } from './firebase-auth.service';
import { take } from 'rxjs/operators';

/** Determines post-login navigation targets and resolves the initial project route from the URL or user context. */
@Injectable({
  providedIn: 'root',
})
export class ProjectRoutingService {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);
  private snackBar = inject(MatSnackBar);
  private rbacService = inject(RbacService);
  private projectCreationService = inject(ProjectCreationService);
  private firebaseAuthService = inject(FirebaseAuthService);

  /**
   * Handle project routing based on user's available projects
   * Issue #304: Add authorization check before opening dialog
   */
  async handleProjectRouting(): Promise<void> {
    try {
      ClientLoggingService.log('INFO', '[ProjectRoutingService] Handling project routing');

      // Issue #304: Check if user is authorized before showing dialog
      const unauthorized = await this.firebaseAuthService.unauthorized$.pipe(take(1)).toPromise();
      if (unauthorized) {
        ClientLoggingService.log('INFO', '[ProjectRoutingService] User is unauthorized, skipping project routing');
        return;
      }

      ClientLoggingService.log(
        'INFO',
        '[ProjectRoutingService] User is authorized, opening project selection dialog...'
      );

      // Open dialog immediately - it will load projects internally
      this.showProjectSelectionDialog();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectRoutingService] Error in project routing:', error);
      this.snackBar.open('Error opening project selection', 'Close', { duration: 3000 });
    }
  }

  /**
   * Show project selection dialog - projects will be loaded by the dialog
   */
  private showProjectSelectionDialog(): void {
    const dialogRef = ProjectSelectionDialogComponent.open(this.dialog, { showCancel: false });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        if (result.action === 'new-project') {
          // Handle new project creation
          await this.handleNewProject();
        } else if (result.action === 'no-projects') {
          // Handle case where user has no projects
          await this.handleNewProject();
        } else if (result.architecturalProjectId) {
          // Handle existing project selection
          ClientLoggingService.log(
            'INFO',
            `[ProjectRoutingService] Project selected: ${result.architecturalProjectId}`
          );
          this.router.navigate(['/projects', result.architecturalProjectId, 'pages', '1', 'overview']);
        }
      } else {
        ClientLoggingService.log('INFO', '[ProjectRoutingService] No project selected, staying on current page');
      }
    });
  }

  /**
   * Handle new project creation workflow
   */
  private async handleNewProject(): Promise<void> {
    try {
      ClientLoggingService.log('INFO', '[ProjectRoutingService] Opening new project dialog...');

      // Show new project creation dialog
      const dialogRef = this.handsetDialog.open(NewProjectDialogComponent, {
        width: '600px',
        disableClose: false,
      });

      dialogRef.afterClosed().subscribe(async (result: NewProjectData | undefined) => {
        if (result) {
          await this.createProject(result);
        } else {
          // If user cancels, try to handle routing again
          await this.handleProjectRouting();
        }
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectRoutingService] Error opening new project dialog:', error);
      this.snackBar.open('Error opening project creation dialog', 'Close', { duration: 3000 });
    }
  }

  /**
   * Handle project creation result from dialog (project was already created in dialog)
   */
  private async createProject(projectData: NewProjectData): Promise<void> {
    try {
      ClientLoggingService.log(
        'INFO',
        '[ProjectRoutingService] Project creation completed in dialog:',
        projectData.projectId
      );

      // The project was already created in the NewProjectDialogComponent
      // We just need to navigate to the newly created project
      if (projectData.projectId) {
        ClientLoggingService.log('INFO', `[ProjectRoutingService] Navigating to project: ${projectData.projectId}`);
        this.router.navigate(['/projects', projectData.projectId, 'pages', '1', 'overview']);
      } else {
        ClientLoggingService.log('ERROR', '[ProjectRoutingService] No project ID provided from dialog');
        this.snackBar.open('Error: No project ID received', 'Close', { duration: 5000 });
        await this.handleProjectRouting();
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectRoutingService] Error navigating to project:', error);
      this.snackBar.open('Error navigating to project', 'Close', { duration: 5000 });
      await this.handleProjectRouting();
    }
  }
}
